<template>
<div id="subscription" v-if="subscription && subscription.id">
  <h2>
    Subscription #{{ subscriptionId }}
    <span
      v-if="subscription.purchase.gift"
    >
      {{ $root.giftIcon }}
    </span>
  </h2>

  <subscription-nav
    :subscriptionId="subscriptionId"
  >
  </subscription-nav>

  <h3>
    Subscription Details
  </h3>

  <table class="table">
    <tr>
      <td>
        Recipient User
      </td>
      <td>
        <router-link :to="{
          name: 'user',
          params: {
            userId: subscription.user_id
          }
        }">
          User #{{ subscription.user_id }}
        </router-link>
      </td>
    </tr>
    <tr>
      <td>
        Recipient Email
      </td>
      <td>
        <a :href="'mailto:' + subscription.email">
          {{ subscription.email }}
        </a>
        <span
          v-if="subscription.purchase.gift"
        >
          {{ $root.giftIcon }}
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Subscription Start Date
      </td>
      <td>
        {{ formattedTime(subscription.start_ts) }}
      </td>
    </tr>
    <tr>
      <td>
        Subscription End Date
      </td>
      <td>
        {{ formattedTime(subscription.end_ts) }}
      </td>
    </tr
    <tr>
      <td>
        Subscription Status
      </td>
      <td>
        {{ subscription.status }}
      </td>
    </tr>
    <tr>
      <td>
        Subscription Ending Soon?
      </td>
      <td>
        {{ subscription.expiring_soon }}
      </td>
    </tr>
    <tr>
      <td>
        Subscription About to Expire?
      </td>
      <td>
        {{ subscription.about_to_expire }}
      </td>
    </tr>
    <tr>
      <td>
        Created At
      </td>
      <td>
        {{ formattedTime(subscription.created_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Updated At
      </td>
      <td>
        {{ formattedTime(subscription.updated_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Purchase
      </td>
      <td
        v-if="subscription.purchase"
      >
        <router-link :to="{
            name: 'purchase',
            params: {
              purchaseId: subscription.purchase_id
            }
          }"
        >
          #{{ subscription.purchase_id }}
          ({{ formattedTime(subscription.purchase.attributes.purchased_at) }})
        </router-link>

        <purchase-status-badge
          :purchase="subscription.purchase.attributes"
          statusPrefix="Purchase"
        />
      </td>
    </tr>
    <tr
      v-if="subscription.purchasable_item"
    >
      <td>
        Purchased Subscription
      </td>
      <td>
        <router-link :to="{
            name: 'purchasable_item',
            params: {
              purchasableItemId: subscription.purchasable_item_id
            }
          }"
        >
          {{ subscription.purchasable_item.attributes.name }}
        </router-link>
      </td>
    </tr>
  </table>

  <hr />

  <h3>Newsletter Delivery History</h3>

  <user-newsletter-delivery-history
    :userId="subscription.user_id"
  />

  <h4>Additional Options</h4>

  <div class="form-group">
    <button
      type="button"
      class="btn btn-primary form-control"
      v-on:click.prevent="resendLatest"
    >
      Resend Latest Puzzle
      <spinner size="small" v-if="resendLoading"></spinner>
    </button>
  </div>

  <div
    class="alert alert-warning text-center"
    role="alert"
  >
    Please note: this will resend the latest puzzle to the subscriber's email address
    <strong>regardless of their subscription status</strong>.
  </div>

  <div class="form-group">
    <div class="form-check">
      <label
        class="form-check-label"
      >
        <input
          type="checkbox"
          name="send_latest"
          v-model="sendLatest"
          class="form-check-input"
        />
        Send last puzzle with lapsed subscription email?
      </label>
    </div>
  </div>

  <div class="form-group">
    <button
      type="button"
      class="btn btn-primary form-control"
      v-on:click.prevent="sendLapsedEmail"
    >
      Send Lapsed Subscription Email
      <spinner size="small" v-if="sendLapsedEmailLoading"></spinner>
    </button>
  </div>
</div>
</template>

<script>
import Spinner from 'vue-simple-spinner'

import PurchaseStatusBadge from '@/components/PurchaseStatusBadge'
import SubscriptionNav from '@/components/SubscriptionNav'
import UserNewsletterDeliveryHistory from '@/components/UserNewsletterDeliveryHistory'

import FlashStore from '@/stores/FlashStore'

import { mapState } from 'vuex'

export default {
  data () {
    return {
      resendLoading: false,
      sendLapsedEmailLoading: false,
      sendLatest: true
    }
  },

  computed: mapState({
    base: state => state.base,
    subscription: state => state.SubscriptionModule.subscription
  }),

  components: {
    'purchase-status-badge': PurchaseStatusBadge,
    'spinner': Spinner,
    'subscription-nav': SubscriptionNav,
    'user-newsletter-delivery-history': UserNewsletterDeliveryHistory
  },

  props: {
    subscriptionId: {
      type: [ Number, String ],
      default: undefined
    }
  },

  created () {
    this.get()
  },

  methods: {
    formattedTime (d) {
      return this.$root.displayDateTimeFmt(d)
    },

    get () {
      this.$store.dispatch(
        `SubscriptionModule/getSubscription`, this.subscriptionId
      ).catch(this.$root.processErrors)
    },

    resendLatest () {
      this.resendLoading = true

      this.$http.post(
        this.apiUrl('/subscriptions/' + this.subscriptionId + '/resend_latest_puzzle', true)
      ).then((resp) => {
        this.resendLoading = false
        FlashStore.result('The latest puzzle email has been sent to the subscriber.')
      }, (resp) => {
        this.resendLoading = false

        this.processErrors(resp)
      })
    },

    sendLapsedEmail () {
      this.sendLapsedEmailLoading = true

      this.$http.post(
        this.apiUrl('/subscriptions/' + this.subscriptionId + '/send_lapsed_subscription_email', true),
        {
          send_latest: this.sendLatest
        }
      ).then((resp) => {
        this.sendLapsedEmailLoading = false
        FlashStore.result('A lapsed subscription email has been sent to the subscriber.')
      }, (resp) => {
        this.sendLapsedEmailLoading = false

        this.processErrors(resp)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Subscription.vue